import React, { useEffect, useMemo } from 'react';
import { useAPIWrapper, DetailBySchema, DetailFieldLayout } from 'drf-react-by-schema';
import { useNavigate, useSearchParams } from 'react-router-dom';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';

import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import HomeIcon from '@mui/icons-material/Home';

import { detailAccordion, detailDadosBasicos, detailNonAccordion } from 'src/styles/layout';
import { Layout } from 'src/styles';

import { useAppSelector } from 'src/app/hooks';
import { setPage } from 'src/features/pageSlice';
import { appDispatch, dateFormatted } from 'src/utils/utils';
import { FlocosProductionStatus, getFlocosProductionLabel } from 'src/constants';

import AsmubipButton from 'src/components/AsmubipButton';
import { PageType } from 'src/constants/PageType';

const MODEL = 'flocos_production';

function FlocosReviewDetail() {
  const { id, formValues, perfil } = useAppSelector((state) => state.page);
  const { pageForm, loadSinglePageData } = useAPIWrapper();
  const navigate = useNavigate();
  const [URLSearchParams] = useSearchParams();
  const savedType = URLSearchParams.get('type');

  const key = MODEL;

  const handleEditClick = () => {
    navigate(`/${key}/edit/${id}`);
  };

  const handleInicioClick = () => {
    navigate('/');
  };

  const fieldsLayout = useMemo(() => {
    if (!formValues?.status) {
      return;
    }

    const status = formValues.status.value as FlocosProductionStatus;

    const newFieldsLayout: DetailFieldLayout[] = [
      {
        title: 'Dados do registro',
        rows: ['lote', 'data_registro', 'perfil', 'ajudantes', 'grupo'],
        isAccordion: true,
        ...detailDadosBasicos,
      },
    ];

    if (
      [FlocosProductionStatus.READY_TO_REVIEW, FlocosProductionStatus.COMPLETE].includes(status)
    ) {
      const pesagemSecoFieldsLayout: DetailFieldLayout = {
        title: 'Pesagem do floco seco',
        rows: ['peso_flocos_seco', 'data_flocos_seco'],
        isAccordion: false,
        isAccordionOpen: true,
        ...detailNonAccordion,
      };
      newFieldsLayout.push(pesagemSecoFieldsLayout);
    }

    const layoutPesagemUmido =
      status === FlocosProductionStatus.IN_PRODUCTION ? detailNonAccordion : detailAccordion;
    const pesagemUmidoFieldsLayout: DetailFieldLayout = {
      title: 'Pesagem do floco úmido',
      rows: ['data_flocos_umido', 'peso_flocos_umido'],
      isAccordion: status !== FlocosProductionStatus.IN_PRODUCTION,
      isAccordionOpen: status === FlocosProductionStatus.IN_PRODUCTION,
      ...layoutPesagemUmido,
    };
    newFieldsLayout.push(pesagemUmidoFieldsLayout);

    const layoutColeta =
      status === FlocosProductionStatus.IN_PRODUCTION ? detailNonAccordion : detailAccordion;
    const coletaFieldsLayout: DetailFieldLayout = {
      title: 'Coleta do Coco',
      rows: ['data_coleta'],
      isAccordion: status !== FlocosProductionStatus.IN_PRODUCTION,
      isAccordionOpen: status === FlocosProductionStatus.IN_PRODUCTION,
      ...layoutColeta,
    };
    newFieldsLayout.push(coletaFieldsLayout);

    return newFieldsLayout;
  }, [formValues?.status]);

  useEffect(() => {
    if (!perfil || !id || !key) {
      return;
    }
    const loadAllDataAsync = async () => {
      const values = await loadSinglePageData({
        model: MODEL,
        objId: id,
      });
      if (values) {
        const pageTitle = !savedType
          ? 'Produção de flocos de mesocarpo'
          : savedType === PageType.CREATE
            ? 'Produção concluída!'
            : 'Produção editada!';
        appDispatch(
          setPage({
            id,
            model: MODEL,
            formValues: values,
            title: pageTitle,
          })
        );
      }
    };
    loadAllDataAsync();
  }, [id, key, savedType, perfil]);

  if (!pageForm?.schema || !pageForm.columns || !formValues || !id || !fieldsLayout) {
    return null;
  }

  return (
    <>
      {savedType && (
        <Card>
          <CardContent sx={Layout.productionDetailCardContent}>
            <Box sx={Layout.productionDetailCardIconWrapper}>
              <CheckIcon sx={Layout.productionCardIcon} />
            </Box>
            <Typography variant="h6">
              Produção de flocos {dateFormatted(formValues.data_flocos_seco)}
            </Typography>
          </CardContent>
          <CardActions>
            <Button
              variant="outlined"
              startIcon={<HomeIcon />}
              sx={Layout.fullWidthButton}
              onClick={handleInicioClick}>
              Início
            </Button>
          </CardActions>
        </Card>
      )}
      <DetailBySchema
        schema={pageForm.schema}
        columns={pageForm.columns}
        values={formValues}
        fieldsLayout={fieldsLayout}
        elevation={0}
      />
      <AsmubipButton
        variant="contained-secondary"
        aria-label="editar"
        size="large"
        sx={{ width: '100%', mt: 2, mb: 2 }}
        onClick={handleEditClick}
        startIcon={<EditIcon />}>
        Editar
      </AsmubipButton>
      <Container sx={Layout.BottomInfoContainer}>
        <Card sx={Layout.BottomInfoContainerCard}>
          <CardContent>
            <Typography variant="subtitle1">{formValues.status.display_name}</Typography>
            <Typography variant="body2">
              {getFlocosProductionLabel({ status: formValues.status.value, helperText: true })}
            </Typography>
          </CardContent>
        </Card>
      </Container>
    </>
  );
}

export default React.memo(FlocosReviewDetail);
